import { useState, useRef, useCallback } from "react";
import { useLocation } from "wouter";
import { useQueryClient } from "@tanstack/react-query";
import { useListCoaches, useCreateContract, getListContractsQueryKey } from "@workspace/api-client-react";
import { useOrg } from "@/lib/orgContext";
import { useFeature } from "@/lib/useFeature";
import { COACH_POSITIONS, toSelectablePosition } from "@/lib/coachPositions";
import { useToast } from "@/hooks/use-toast";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Separator } from "@/components/ui/separator";
import {
  ArrowLeft, Upload, FileText, CheckCircle2, XCircle, Loader2,
  Download, AlertTriangle, Info, Eye, ChevronRight, ChevronLeft,
  Plus, Trash2, RotateCcw
} from "lucide-react";
import { Link } from "wouter";
import { useEffect } from "react";
import type { ContractBonus, TerminationRules } from "@workspace/api-client-react";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type Confidence = "high" | "medium" | "low";

interface ExtractedField<T = string | number | boolean | null> {
  value: T;
  confidence: Confidence;
  snippet: string;
}

interface ExtractionResult {
  coachFirstName: ExtractedField<string>;
  coachLastName: ExtractedField<string>;
  coachEmail: ExtractedField<string>;
  coachTitle: ExtractedField<string>;
  contractType: ExtractedField<string>;
  startDate: ExtractedField<string>;
  endDate: ExtractedField<string>;
  annualSalary: ExtractedField<number | null>;
  buyoutClause: ExtractedField<number | null>;
  terminationConditions: ExtractedField<string>;
  mitigationRequired: ExtractedField<boolean>;
  notes: ExtractedField<string>;
  bonuses: ExtractedField<ContractBonus[]>;
  buyoutSchedule: ExtractedField<{ year: number; amount: number }[]>;
  terminationRules: ExtractedField<{
    withoutCauseUniversity: string;
    withCause: string;
    coachInitiated: string;
  }>;
}

type PdfFileStatus = "pending" | "uploading" | "extracting" | "review" | "saving" | "saved" | "error";

interface PdfFileItem {
  id: string;
  file: File;
  blobUrl: string;
  status: PdfFileStatus;
  error?: string;
  extraction?: ExtractionResult;
}

interface PreviewRow {
  row: number;
  data: Record<string, string>;
  errors: { field: string; message: string }[];
  coachFound?: boolean;
  coachName?: string;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function ConfidenceBadge({ confidence, snippet }: { confidence: Confidence; snippet?: string }) {
  const colors: Record<Confidence, string> = {
    high: "bg-green-100 text-green-800 border-green-200",
    medium: "bg-yellow-100 text-yellow-800 border-yellow-200",
    low: "bg-red-100 text-red-800 border-red-200",
  };
  const badge = (
    <Badge variant="outline" className={`text-[10px] px-1.5 py-0.5 ${colors[confidence]}`}>
      {confidence}
    </Badge>
  );
  if (!snippet) return badge;
  return (
    <Tooltip>
      <TooltipTrigger asChild>{badge}</TooltipTrigger>
      <TooltipContent side="top" className="max-w-xs text-xs">
        <span className="font-medium">Source: </span>"{snippet}"
      </TooltipContent>
    </Tooltip>
  );
}

function FieldRow({
  label,
  confidence,
  snippet,
  children,
  id,
  edited,
}: {
  label: string;
  confidence?: Confidence;
  snippet?: string;
  children: React.ReactNode;
  id?: string;
  edited?: boolean;
}) {
  return (
    <div className="space-y-1 scroll-mt-32" id={id}>
      <div className="flex items-center gap-2">
        <Label className="text-xs font-medium text-muted-foreground">{label}</Label>
        {edited ? (
          <Badge variant="outline" className="text-[10px] px-1.5 py-0.5 bg-blue-100 text-blue-800 border-blue-200">
            edited
          </Badge>
        ) : (
          confidence && <ConfidenceBadge confidence={confidence} snippet={snippet} />
        )}
      </div>
      {children}
    </div>
  );
}

// ---------------------------------------------------------------------------
// PDF Review Form
// ---------------------------------------------------------------------------

function PdfReviewForm({
  item,
  importBatchId,
  onSave,
  onBack,
}: {
  item: PdfFileItem;
  importBatchId: string;
  onSave: (id: string, contractId: number) => void;
  onBack: () => void;
}) {
  const { toast } = useToast();
  const { data: coaches } = useListCoaches();
  const createContract = useCreateContract();
  const queryClient = useQueryClient();
  const ex = item.extraction!;

  const [coachMatchMode, setCoachMatchMode] = useState<"existing" | "create">("existing");
  const [selectedCoachId, setSelectedCoachId] = useState<number | null>(null);

  // Form state - pre-filled from extraction
  const [contractType, setContractType] = useState<string>(toSelectablePosition(ex.contractType.value));
  const [status, setStatus] = useState<"active" | "pending">("active");
  const [startDate, setStartDate] = useState(ex.startDate.value || "");
  const [endDate, setEndDate] = useState(ex.endDate.value || "");
  const [annualSalary, setAnnualSalary] = useState(String(ex.annualSalary.value ?? ""));
  const [buyoutClause, setBuyoutClause] = useState(String(ex.buyoutClause.value ?? ""));
  const [terminationConditions, setTerminationConditions] = useState(ex.terminationConditions.value || "");
  const [notes, setNotes] = useState(ex.notes.value || "");
  const [mitigationRequired, setMitigationRequired] = useState(ex.mitigationRequired.value ?? false);
  const [bonuses, setBonuses] = useState<ContractBonus[]>(
    (ex.bonuses.value ?? []).map((b, i) => ({ ...b, id: `pdf-${i}-${Date.now()}` }))
  );
  const [buyoutSchedule, setBuyoutSchedule] = useState(ex.buyoutSchedule.value ?? []);
  const [terminationRules, setTerminationRules] = useState(
    ex.terminationRules.value ?? { withoutCauseUniversity: "schedule", withCause: "none", coachInitiated: "none" }
  );

  const [editedFields, setEditedFields] = useState<Set<string>>(new Set());
  const markEdited = useCallback((key: string) => {
    setEditedFields((prev) => {
      if (prev.has(key)) return prev;
      const next = new Set(prev);
      next.add(key);
      return next;
    });
  }, []);

  // Build field metadata for the summary panel. Coach name/email fields are
  // display-only (cannot be edited here), so their confidence is fixed.
  const summaryFields: { key: string; label: string; confidence: Confidence; editable: boolean }[] = [
    { key: "coachFirstName", label: "First name", confidence: ex.coachFirstName.confidence, editable: false },
    { key: "coachLastName", label: "Last name", confidence: ex.coachLastName.confidence, editable: false },
    { key: "coachEmail", label: "Email", confidence: ex.coachEmail.confidence, editable: false },
    { key: "contractType", label: "Contract Type", confidence: ex.contractType.confidence, editable: true },
    { key: "startDate", label: "Start Date", confidence: ex.startDate.confidence, editable: true },
    { key: "endDate", label: "End Date", confidence: ex.endDate.confidence, editable: true },
    { key: "annualSalary", label: "Annual Salary", confidence: ex.annualSalary.confidence, editable: true },
    { key: "buyoutClause", label: "Buyout Clause", confidence: ex.buyoutClause.confidence, editable: true },
    ...(bonuses.length > 0
      ? [{ key: "bonuses", label: "Bonuses", confidence: ex.bonuses.confidence, editable: true }]
      : []),
    ...(buyoutSchedule.length > 0
      ? [{ key: "buyoutSchedule", label: "Buyout Schedule", confidence: ex.buyoutSchedule.confidence, editable: true }]
      : []),
    { key: "terminationRules", label: "Termination Rules", confidence: ex.terminationRules.confidence, editable: true },
    { key: "mitigationRequired", label: "Mitigation Required", confidence: ex.mitigationRequired.confidence, editable: true },
    { key: "terminationConditions", label: "Termination Conditions", confidence: ex.terminationConditions.confidence, editable: true },
    { key: "notes", label: "Notes", confidence: ex.notes.confidence, editable: true },
  ];

  let highCount = 0;
  let mediumCount = 0;
  let lowCount = 0;
  let verifiedCount = 0;
  const lowFields: { key: string; label: string }[] = [];
  for (const f of summaryFields) {
    if (f.editable && editedFields.has(f.key)) {
      verifiedCount++;
    } else if (f.confidence === "high") {
      highCount++;
    } else if (f.confidence === "medium") {
      mediumCount++;
    } else {
      lowCount++;
      lowFields.push({ key: f.key, label: f.label });
    }
  }
  const totalFields = summaryFields.length;

  const jumpTo = (key: string) => {
    const el = document.getElementById(`field-${key}`);
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  // Try to auto-match coach by email
  useEffect(() => {
    if (!coaches) return;
    const email = ex.coachEmail.value?.toLowerCase();
    if (email) {
      const match = coaches.find((c) => c.email?.toLowerCase() === email);
      if (match) {
        setSelectedCoachId(match.id);
        setCoachMatchMode("existing");
        return;
      }
    }
    setCoachMatchMode("existing");
  }, [coaches, ex.coachEmail.value]);

  const handleSave = async () => {
    if (!selectedCoachId) {
      toast({ title: "Select a coach", description: "Choose an existing coach or create a new one.", variant: "destructive" });
      return;
    }
    if (!startDate || !endDate || !annualSalary) {
      toast({ title: "Missing required fields", description: "Start date, end date, and salary are required.", variant: "destructive" });
      return;
    }

    try {
      const contract = await createContract.mutateAsync({
        data: {
          coachId: selectedCoachId,
          contractType: toSelectablePosition(contractType),
          status,
          startDate,
          endDate,
          annualSalary: Number(annualSalary),
          buyoutClause: buyoutClause ? Number(buyoutClause) : undefined,
          terminationConditions: terminationConditions || undefined,
          notes: notes || undefined,
          bonuses,
          allowances: [],
          buyoutSchedule,
          terminationRules: terminationRules as Parameters<typeof createContract.mutateAsync>[0]["data"]["terminationRules"],
          mitigationRequired,
          importSource: "pdf_extraction",
          importBatchId,
        },
      });
      await queryClient.invalidateQueries({ queryKey: getListContractsQueryKey() });
      onSave(item.id, contract.id);
    } catch {
      toast({ title: "Failed to save contract", description: "Please check the fields and try again.", variant: "destructive" });
    }
  };

  const extractedEmail = ex.coachEmail.value;
  const emailMatchedCoach = coaches?.find((c) => c.email?.toLowerCase() === extractedEmail?.toLowerCase());

  return (
    <div className="flex flex-col lg:flex-row gap-4 h-full min-h-0">
      {/* Left: PDF Viewer */}
      <div className="lg:w-1/2 flex flex-col gap-2">
        <div className="flex items-center justify-between">
          <Button variant="ghost" size="sm" onClick={onBack} className="gap-1.5">
            <ChevronLeft className="h-4 w-4" />
            Back to queue
          </Button>
          <span className="text-sm text-muted-foreground truncate max-w-[200px]">{item.file.name}</span>
        </div>
        <div className="flex-1 border rounded-lg overflow-hidden bg-muted/20" style={{ minHeight: "600px" }}>
          <iframe
            src={item.blobUrl}
            className="w-full h-full"
            title="Contract PDF"
            style={{ minHeight: "600px" }}
          />
        </div>
      </div>

      {/* Right: Extracted Fields Form */}
      <div className="lg:w-1/2 overflow-y-auto space-y-4 pr-1 relative">
        <div>
          <h3 className="font-semibold text-lg">Review Extracted Fields</h3>
          <p className="text-sm text-muted-foreground">
            Confidence badges show AI certainty. Hover for source text. Edit any incorrect values before saving.
          </p>
        </div>

        {/* Sticky confidence summary bar */}
        <div className="sticky top-0 z-10 -mx-1 px-1 pb-2 pt-2 bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/80 border-b">
          <div className="rounded-md border bg-card p-2.5 space-y-2 shadow-sm">
            <div className="flex items-center justify-between gap-2 flex-wrap">
              <div className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground">
                <Info className="h-3.5 w-3.5" />
                Extraction Confidence ({totalFields} fields)
              </div>
              <div className="flex items-center gap-1.5 flex-wrap">
                <Badge variant="outline" className="text-[10px] px-1.5 py-0.5 bg-green-100 text-green-800 border-green-200">
                  {highCount} high
                </Badge>
                <Badge variant="outline" className="text-[10px] px-1.5 py-0.5 bg-yellow-100 text-yellow-800 border-yellow-200">
                  {mediumCount} medium
                </Badge>
                <Badge variant="outline" className="text-[10px] px-1.5 py-0.5 bg-red-100 text-red-800 border-red-200">
                  {lowCount} low
                </Badge>
                {verifiedCount > 0 && (
                  <Badge variant="outline" className="text-[10px] px-1.5 py-0.5 bg-blue-100 text-blue-800 border-blue-200">
                    {verifiedCount} edited
                  </Badge>
                )}
              </div>
            </div>
            {lowFields.length > 0 ? (
              <div className="flex items-start gap-1.5 flex-wrap pt-1 border-t">
                <span className="text-[11px] text-muted-foreground pt-0.5 inline-flex items-center gap-1">
                  <AlertTriangle className="h-3 w-3 text-red-600" />
                  Review:
                </span>
                {lowFields.map((f) => (
                  <button
                    key={f.key}
                    type="button"
                    onClick={() => jumpTo(f.key)}
                    className="text-[11px] px-1.5 py-0.5 rounded border border-red-200 bg-red-50 text-red-800 hover:bg-red-100 transition-colors"
                  >
                    {f.label}
                  </button>
                ))}
              </div>
            ) : (
              <div className="flex items-center gap-1.5 pt-1 border-t text-[11px] text-green-700">
                <CheckCircle2 className="h-3 w-3" />
                No low-confidence fields remaining.
              </div>
            )}
          </div>
        </div>

        {/* Coach Section */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm">Coach</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="grid grid-cols-2 gap-2 text-sm">
              <div id="field-coachFirstName" className="scroll-mt-32">
                <div className="text-xs text-muted-foreground mb-1 flex items-center gap-1">
                  First name <ConfidenceBadge confidence={ex.coachFirstName.confidence} snippet={ex.coachFirstName.snippet} />
                </div>
                <div className="font-medium">{ex.coachFirstName.value || "—"}</div>
              </div>
              <div id="field-coachLastName" className="scroll-mt-32">
                <div className="text-xs text-muted-foreground mb-1 flex items-center gap-1">
                  Last name <ConfidenceBadge confidence={ex.coachLastName.confidence} snippet={ex.coachLastName.snippet} />
                </div>
                <div className="font-medium">{ex.coachLastName.value || "—"}</div>
              </div>
            </div>
            <div className="text-sm scroll-mt-32" id="field-coachEmail">
              <div className="text-xs text-muted-foreground mb-1 flex items-center gap-1">
                Email <ConfidenceBadge confidence={ex.coachEmail.confidence} snippet={ex.coachEmail.snippet} />
              </div>
              <div>{ex.coachEmail.value || "—"}</div>
            </div>

            {emailMatchedCoach && (
              <div className="rounded-md bg-green-50 border border-green-200 p-2 text-sm text-green-800 flex items-center gap-2">
                <CheckCircle2 className="h-4 w-4 text-green-600 shrink-0" />
                Matched to existing coach: <strong>{emailMatchedCoach.firstName} {emailMatchedCoach.lastName}</strong>
              </div>
            )}

            <div className="space-y-2">
              <Label className="text-xs">Link to coach *</Label>
              <div className="flex gap-2">
                <Button
                  size="sm"
                  variant={coachMatchMode === "existing" ? "default" : "outline"}
                  onClick={() => setCoachMatchMode("existing")}
                  className="flex-1"
                >
                  Existing Coach
                </Button>
                <Button
                  size="sm"
                  variant={coachMatchMode === "create" ? "default" : "outline"}
                  onClick={() => setCoachMatchMode("create")}
                  className="flex-1"
                  disabled
                  title="Create coach first, then return to import"
                >
                  Create New
                </Button>
              </div>
              {coachMatchMode === "existing" && (
                <Select
                  value={selectedCoachId ? String(selectedCoachId) : ""}
                  onValueChange={(v) => setSelectedCoachId(Number(v))}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select a coach..." />
                  </SelectTrigger>
                  <SelectContent>
                    {coaches?.map((c) => (
                      <SelectItem key={c.id} value={String(c.id)}>
                        {c.firstName} {c.lastName} — {c.sport}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
              {coachMatchMode === "create" && (
                <div className="text-xs text-muted-foreground border rounded p-2 bg-muted/40">
                  <Link href="/coaches/new" className="text-primary underline">Create the coach profile first</Link>{" "}
                  then return here to import the contract.
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Contract Terms */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm">Contract Terms</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <FieldRow label="Contract Type *" confidence={ex.contractType.confidence} snippet={ex.contractType.snippet} id="field-contractType" edited={editedFields.has("contractType")}>
                <Select value={contractType} onValueChange={(v) => { setContractType(v); markEdited("contractType"); }}>
                  <SelectTrigger className="h-8 text-sm"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {COACH_POSITIONS.map((p) => (
                      <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </FieldRow>
              <FieldRow label="Initial Status *">
                <Select value={status} onValueChange={(v) => setStatus(v as "active" | "pending")}>
                  <SelectTrigger className="h-8 text-sm"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="active">Active</SelectItem>
                    <SelectItem value="pending">Pending</SelectItem>
                  </SelectContent>
                </Select>
              </FieldRow>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <FieldRow label="Start Date *" confidence={ex.startDate.confidence} snippet={ex.startDate.snippet} id="field-startDate" edited={editedFields.has("startDate")}>
                <Input type="date" className="h-8 text-sm" value={startDate} onChange={(e) => { setStartDate(e.target.value); markEdited("startDate"); }} />
              </FieldRow>
              <FieldRow label="End Date *" confidence={ex.endDate.confidence} snippet={ex.endDate.snippet} id="field-endDate" edited={editedFields.has("endDate")}>
                <Input type="date" className="h-8 text-sm" value={endDate} onChange={(e) => { setEndDate(e.target.value); markEdited("endDate"); }} />
              </FieldRow>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <FieldRow label="Annual Salary ($) *" confidence={ex.annualSalary.confidence} snippet={ex.annualSalary.snippet} id="field-annualSalary" edited={editedFields.has("annualSalary")}>
                <Input
                  type="number"
                  className="h-8 text-sm"
                  value={annualSalary}
                  onChange={(e) => { setAnnualSalary(e.target.value); markEdited("annualSalary"); }}
                  placeholder="0"
                />
              </FieldRow>
              <FieldRow label="Buyout Clause ($)" confidence={ex.buyoutClause.confidence} snippet={ex.buyoutClause.snippet} id="field-buyoutClause" edited={editedFields.has("buyoutClause")}>
                <Input
                  type="number"
                  className="h-8 text-sm"
                  value={buyoutClause}
                  onChange={(e) => { setBuyoutClause(e.target.value); markEdited("buyoutClause"); }}
                  placeholder="0"
                />
              </FieldRow>
            </div>
          </CardContent>
        </Card>

        {/* Bonuses */}
        {bonuses.length > 0 && (
          <Card id="field-bonuses" className="scroll-mt-32">
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <CardTitle className="text-sm flex items-center gap-2">
                  Bonuses
                  {editedFields.has("bonuses") ? (
                    <Badge variant="outline" className="text-[10px] px-1.5 py-0.5 bg-blue-100 text-blue-800 border-blue-200">edited</Badge>
                  ) : (
                    <ConfidenceBadge confidence={ex.bonuses.confidence} snippet={ex.bonuses.snippet} />
                  )}
                </CardTitle>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-6 text-xs"
                  onClick={() => { setBonuses([]); markEdited("bonuses"); }}
                >
                  Clear all
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {bonuses.map((bonus, idx) => (
                  <div key={bonus.id} className="flex items-center gap-2 text-sm border rounded p-2">
                    <div className="flex-1 min-w-0">
                      <div className="font-medium truncate">{bonus.label}</div>
                      <div className="text-xs text-muted-foreground">
                        ${bonus.amount.toLocaleString()} · {bonus.type} · {bonus.paymentTiming}
                      </div>
                    </div>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7 shrink-0"
                      onClick={() => { setBonuses(bonuses.filter((_, i) => i !== idx)); markEdited("bonuses"); }}
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Buyout Schedule */}
        {buyoutSchedule.length > 0 && (
          <Card id="field-buyoutSchedule" className="scroll-mt-32">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm flex items-center gap-2">
                Buyout Schedule
                {editedFields.has("buyoutSchedule") ? (
                  <Badge variant="outline" className="text-[10px] px-1.5 py-0.5 bg-blue-100 text-blue-800 border-blue-200">edited</Badge>
                ) : (
                  <ConfidenceBadge confidence={ex.buyoutSchedule.confidence} snippet={ex.buyoutSchedule.snippet} />
                )}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-1.5">
                {buyoutSchedule.map((entry, idx) => (
                  <div key={idx} className="flex items-center gap-2 text-sm">
                    <span className="text-muted-foreground w-16 shrink-0">Year {entry.year}:</span>
                    <Input
                      type="number"
                      className="h-7 text-sm flex-1"
                      value={String(entry.amount)}
                      onChange={(e) => {
                        const updated = [...buyoutSchedule];
                        updated[idx] = { ...entry, amount: Number(e.target.value) };
                        setBuyoutSchedule(updated);
                        markEdited("buyoutSchedule");
                      }}
                    />
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7 shrink-0"
                      onClick={() => { setBuyoutSchedule(buyoutSchedule.filter((_, i) => i !== idx)); markEdited("buyoutSchedule"); }}
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Termination Rules */}
        <Card id="field-terminationRules" className="scroll-mt-32">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2">
              Termination Rules
              {editedFields.has("terminationRules") ? (
                <Badge variant="outline" className="text-[10px] px-1.5 py-0.5 bg-blue-100 text-blue-800 border-blue-200">edited</Badge>
              ) : (
                <ConfidenceBadge confidence={ex.terminationRules.confidence} snippet={ex.terminationRules.snippet} />
              )}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {(["withoutCauseUniversity", "withCause", "coachInitiated"] as const).map((key) => {
              const labels: Record<string, string> = {
                withoutCauseUniversity: "Without Cause (University)",
                withCause: "With Cause",
                coachInitiated: "Coach-Initiated",
              };
              return (
                <div key={key} className="flex items-center gap-2 text-sm">
                  <span className="w-40 text-xs text-muted-foreground shrink-0">{labels[key]}</span>
                  <Select
                    value={terminationRules[key]}
                    onValueChange={(v) => { setTerminationRules({ ...terminationRules, [key]: v }); markEdited("terminationRules"); }}
                  >
                    <SelectTrigger className="h-7 text-xs flex-1"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="schedule">Schedule</SelectItem>
                      <SelectItem value="remaining_salary">Remaining Salary</SelectItem>
                      <SelectItem value="fixed">Fixed</SelectItem>
                      <SelectItem value="none">None</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              );
            })}
            <div className="flex items-center gap-2 text-sm pt-1 scroll-mt-32" id="field-mitigationRequired">
              <input
                type="checkbox"
                id="mitigation"
                checked={mitigationRequired}
                onChange={(e) => { setMitigationRequired(e.target.checked); markEdited("mitigationRequired"); }}
                className="rounded border-input"
              />
              <label htmlFor="mitigation" className="text-xs text-muted-foreground cursor-pointer flex items-center gap-1">
                Mitigation required
                {editedFields.has("mitigationRequired") ? (
                  <Badge variant="outline" className="text-[10px] px-1.5 py-0.5 bg-blue-100 text-blue-800 border-blue-200">edited</Badge>
                ) : (
                  <ConfidenceBadge confidence={ex.mitigationRequired.confidence} snippet={ex.mitigationRequired.snippet} />
                )}
              </label>
            </div>
          </CardContent>
        </Card>

        {/* Termination Conditions + Notes */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm">Provisions & Notes</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <FieldRow label="Termination Conditions" confidence={ex.terminationConditions.confidence} snippet={ex.terminationConditions.snippet} id="field-terminationConditions" edited={editedFields.has("terminationConditions")}>
              <Textarea
                className="text-sm min-h-[80px]"
                value={terminationConditions}
                onChange={(e) => { setTerminationConditions(e.target.value); markEdited("terminationConditions"); }}
                placeholder="Termination conditions..."
              />
            </FieldRow>
            <FieldRow label="Notes" confidence={ex.notes.confidence} snippet={ex.notes.snippet} id="field-notes" edited={editedFields.has("notes")}>
              <Textarea
                className="text-sm min-h-[60px]"
                value={notes}
                onChange={(e) => { setNotes(e.target.value); markEdited("notes"); }}
                placeholder="Internal notes..."
              />
            </FieldRow>
          </CardContent>
        </Card>

        <div className="flex gap-3 pb-6">
          <Button variant="outline" onClick={onBack} className="flex-1">
            Back
          </Button>
          <Button
            onClick={handleSave}
            disabled={createContract.isPending || !selectedCoachId}
            className="flex-1"
          >
            {createContract.isPending ? (
              <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Saving…</>
            ) : (
              <><CheckCircle2 className="h-4 w-4 mr-2" /> Confirm & Save Contract</>
            )}
          </Button>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// PDF Queue View
// ---------------------------------------------------------------------------

function statusIcon(status: PdfFileStatus) {
  switch (status) {
    case "pending": return <div className="h-4 w-4 rounded-full border-2 border-muted-foreground" />;
    case "uploading": return <Loader2 className="h-4 w-4 animate-spin text-blue-500" />;
    case "extracting": return <Loader2 className="h-4 w-4 animate-spin text-purple-500" />;
    case "review": return <Eye className="h-4 w-4 text-orange-500" />;
    case "saving": return <Loader2 className="h-4 w-4 animate-spin text-blue-500" />;
    case "saved": return <CheckCircle2 className="h-4 w-4 text-green-500" />;
    case "error": return <XCircle className="h-4 w-4 text-red-500" />;
  }
}

function statusLabel(status: PdfFileStatus) {
  switch (status) {
    case "pending": return "Queued";
    case "uploading": return "Uploading…";
    case "extracting": return "Extracting…";
    case "review": return "Ready for review";
    case "saving": return "Saving…";
    case "saved": return "Saved";
    case "error": return "Error";
  }
}

// ---------------------------------------------------------------------------
// PDF Tab
// ---------------------------------------------------------------------------

function PdfTab() {
  const { toast } = useToast();
  const [files, setFiles] = useState<PdfFileItem[]>([]);
  const [reviewingId, setReviewingId] = useState<string | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const importBatchIdRef = useRef<string>(
    typeof crypto !== "undefined" && "randomUUID" in crypto
      ? crypto.randomUUID()
      : `pdf-batch-${Date.now()}-${Math.random().toString(36).slice(2)}`
  );

  const addFiles = (newFiles: File[]) => {
    const pdfFiles = newFiles.filter((f) => f.type === "application/pdf" || f.name.endsWith(".pdf"));
    if (pdfFiles.length === 0) {
      toast({ title: "PDFs only", description: "Please drop PDF files only.", variant: "destructive" });
      return;
    }
    const items: PdfFileItem[] = pdfFiles.map((f) => ({
      id: `pdf-${Date.now()}-${Math.random().toString(36).slice(2)}`,
      file: f,
      blobUrl: URL.createObjectURL(f),
      status: "pending",
    }));
    setFiles((prev) => [...prev, ...items]);
  };

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    addFiles(Array.from(e.dataTransfer.files));
  }, []);

  const extractFile = async (item: PdfFileItem) => {
    setFiles((prev) => prev.map((f) => f.id === item.id ? { ...f, status: "extracting" as PdfFileStatus } : f));

    const formData = new FormData();
    formData.append("file", item.file);

    try {
      const res = await fetch("/api/import/contracts/extract-pdf", {
        method: "POST",
        body: formData,
      });
      const data = await res.json() as { extraction?: ExtractionResult; error?: string };
      if (!res.ok || data.error) {
        setFiles((prev) => prev.map((f) => f.id === item.id
          ? { ...f, status: "error" as PdfFileStatus, error: data.error ?? "Extraction failed" }
          : f
        ));
        return;
      }
      setFiles((prev) => prev.map((f) => f.id === item.id
        ? { ...f, status: "review" as PdfFileStatus, extraction: data.extraction }
        : f
      ));
    } catch {
      setFiles((prev) => prev.map((f) => f.id === item.id
        ? { ...f, status: "error" as PdfFileStatus, error: "Network error" }
        : f
      ));
    }
  };

  const handleExtractAll = () => {
    const pending = files.filter((f) => f.status === "pending");
    for (const item of pending) {
      void extractFile(item);
    }
  };

  const handleSaved = (id: string, contractId: number) => {
    setFiles((prev) => prev.map((f) => f.id === id ? { ...f, status: "saved" as PdfFileStatus } : f));
    setReviewingId(null);
    toast({
      title: "Contract saved",
      description: (
        <span>
          Contract saved successfully.{" "}
          <a href={`/contracts/${contractId}`} className="underline">View contract</a>
        </span>
      ) as unknown as string,
    });
  };

  const reviewingItem = files.find((f) => f.id === reviewingId);

  if (reviewingItem && reviewingItem.extraction) {
    return (
      <PdfReviewForm
        item={reviewingItem}
        importBatchId={importBatchIdRef.current}
        onSave={handleSaved}
        onBack={() => setReviewingId(null)}
      />
    );
  }

  return (
    <div className="space-y-4">
      {/* Drop zone */}
      <div
        className={`border-2 border-dashed rounded-lg p-10 text-center cursor-pointer transition-colors ${
          isDragging ? "border-primary bg-primary/5" : "border-muted-foreground/30 hover:border-primary/50 hover:bg-muted/30"
        }`}
        onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
        onDragLeave={() => setIsDragging(false)}
        onDrop={handleDrop}
        onClick={() => fileInputRef.current?.click()}
      >
        <Upload className="h-10 w-10 text-muted-foreground mx-auto mb-3" />
        <p className="font-medium text-sm">Drop contract PDFs here, or click to browse</p>
        <p className="text-xs text-muted-foreground mt-1">Supports multiple files · Max 25 MB per file · Text-based PDFs only</p>
        <input
          ref={fileInputRef}
          type="file"
          accept="application/pdf,.pdf"
          multiple
          className="hidden"
          onChange={(e) => addFiles(Array.from(e.target.files ?? []))}
        />
      </div>

      {files.length > 0 && (
        <>
          {/* File queue */}
          <div className="border rounded-lg divide-y overflow-hidden">
            {files.map((item) => (
              <div key={item.id} className="flex items-center gap-3 px-4 py-3 bg-background hover:bg-muted/30 transition-colors">
                <FileText className="h-5 w-5 text-muted-foreground shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium truncate">{item.file.name}</div>
                  <div className="text-xs text-muted-foreground">
                    {(item.file.size / 1024 / 1024).toFixed(2)} MB
                    {item.error && <span className="text-red-600 ml-2">{item.error}</span>}
                  </div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  {statusIcon(item.status)}
                  <span className="text-xs text-muted-foreground w-28 text-right">{statusLabel(item.status)}</span>
                </div>
                {item.status === "pending" && (
                  <Button size="sm" variant="outline" className="shrink-0" onClick={() => extractFile(item)}>
                    Extract
                  </Button>
                )}
                {item.status === "review" && (
                  <Button size="sm" className="shrink-0" onClick={() => setReviewingId(item.id)}>
                    <Eye className="h-3.5 w-3.5 mr-1.5" />
                    Review
                  </Button>
                )}
                {item.status === "error" && (
                  <Button size="sm" variant="outline" className="shrink-0" onClick={() => {
                    setFiles((prev) => prev.map((f) => f.id === item.id ? { ...f, status: "pending", error: undefined } : f));
                  }}>
                    <RotateCcw className="h-3.5 w-3.5 mr-1" />
                    Retry
                  </Button>
                )}
                {item.status === "saved" && (
                  <Badge className="shrink-0 bg-green-100 text-green-800 border-green-200 hover:bg-green-100">Saved</Badge>
                )}
                <Button
                  size="icon"
                  variant="ghost"
                  className="h-7 w-7 shrink-0 text-muted-foreground"
                  onClick={() => {
                    URL.revokeObjectURL(item.blobUrl);
                    setFiles((prev) => prev.filter((f) => f.id !== item.id));
                  }}
                >
                  <XCircle className="h-4 w-4" />
                </Button>
              </div>
            ))}
          </div>

          {/* Bulk extract button */}
          {files.some((f) => f.status === "pending") && (
            <div className="flex justify-end">
              <Button onClick={handleExtractAll}>
                <Plus className="h-4 w-4 mr-2" />
                Extract All Pending ({files.filter((f) => f.status === "pending").length})
              </Button>
            </div>
          )}

          {/* Summary */}
          <div className="flex gap-3 text-xs text-muted-foreground">
            {(["saved", "review", "error", "extracting", "pending"] as PdfFileStatus[]).map((s) => {
              const count = files.filter((f) => f.status === s).length;
              if (!count) return null;
              return <span key={s}>{statusLabel(s)}: <strong>{count}</strong></span>;
            })}
          </div>
        </>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Spreadsheet Tab
// ---------------------------------------------------------------------------

function SpreadsheetTab() {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<{
    rows: PreviewRow[];
    validCount: number;
    errorCount: number;
  } | null>(null);
  const [isPreviewing, setIsPreviewing] = useState(false);
  const [isImporting, setIsImporting] = useState(false);
  const [importResult, setImportResult] = useState<{ imported: number; total: number; errors: { row: number; message: string }[] } | null>(null);

  const handleDownloadTemplate = async () => {
    try {
      const res = await fetch("/api/import/contracts/template");
      if (!res.ok) throw new Error("Failed");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "contracts-import-template.csv";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch {
      toast({ title: "Download failed", description: "Could not download template.", variant: "destructive" });
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] ?? null;
    setSelectedFile(file);
    setPreview(null);
    setImportResult(null);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    const file = e.dataTransfer.files[0];
    if (file && (file.name.endsWith(".csv") || file.name.endsWith(".xlsx"))) {
      setSelectedFile(file);
      setPreview(null);
      setImportResult(null);
    } else {
      toast({ title: "CSV files only", description: "Please drop a .csv file.", variant: "destructive" });
    }
  };

  const handlePreview = async () => {
    if (!selectedFile) return;
    setIsPreviewing(true);
    setPreview(null);
    try {
      const formData = new FormData();
      formData.append("file", selectedFile);
      const res = await fetch("/api/import/contracts?preview=true", { method: "POST", body: formData });
      const data = await res.json() as { preview?: PreviewRow[]; validCount?: number; errorCount?: number; error?: string };
      if (!res.ok || data.error) {
        toast({ title: "Preview failed", description: data.error ?? "Could not parse file.", variant: "destructive" });
        return;
      }
      setPreview({ rows: data.preview ?? [], validCount: data.validCount ?? 0, errorCount: data.errorCount ?? 0 });
    } catch {
      toast({ title: "Preview failed", description: "Could not connect to server.", variant: "destructive" });
    } finally {
      setIsPreviewing(false);
    }
  };

  const handleImport = async () => {
    if (!selectedFile) return;
    setIsImporting(true);
    try {
      const formData = new FormData();
      formData.append("file", selectedFile);
      const res = await fetch("/api/import/contracts", { method: "POST", body: formData });
      const data = await res.json() as { imported?: number; total?: number; errors?: { row: number; message: string }[]; error?: string };
      if (!res.ok || data.error) {
        toast({ title: "Import failed", description: data.error ?? "Unexpected error.", variant: "destructive" });
        return;
      }
      setImportResult({ imported: data.imported ?? 0, total: data.total ?? 0, errors: data.errors ?? [] });
      if ((data.imported ?? 0) > 0) {
        await queryClient.invalidateQueries({ queryKey: getListContractsQueryKey() });
        toast({ title: `Imported ${data.imported} contract${data.imported !== 1 ? "s" : ""}` });
      }
      setPreview(null);
    } catch {
      toast({ title: "Import failed", description: "Could not connect to server.", variant: "destructive" });
    } finally {
      setIsImporting(false);
    }
  };

  return (
    <div className="space-y-4">
      {/* Template download */}
      <div className="flex items-center justify-between rounded-md border p-3 bg-muted/40">
        <div className="space-y-1">
          <div className="text-sm font-medium flex items-center gap-2">
            <FileText className="h-4 w-4 text-muted-foreground" />
            Download the CSV template
          </div>
          <div className="text-xs text-muted-foreground">
            Includes all contract fields: bonuses, buyout schedule, termination rules, and more.
            Comment lines starting with # explain each column.
          </div>
        </div>
        <Button variant="outline" size="sm" onClick={handleDownloadTemplate}>
          <Download className="h-4 w-4 mr-1" />
          Template
        </Button>
      </div>

      {/* Supported fields info */}
      <div className="rounded-md border p-3 bg-blue-50/50 border-blue-100">
        <div className="text-xs font-medium text-blue-800 mb-1.5 flex items-center gap-1.5">
          <Info className="h-3.5 w-3.5" />
          Supported fields in expanded CSV
        </div>
        <div className="grid grid-cols-2 gap-x-4 gap-y-0.5 text-xs text-blue-700">
          <span>• coach_email (required)</span>
          <span>• contract_type (required)</span>
          <span>• start_date / end_date</span>
          <span>• annual_salary</span>
          <span>• buyout_clause</span>
          <span>• termination_conditions</span>
          <span>• bonuses_json (JSON array)</span>
          <span>• buyout_schedule_json (JSON array)</span>
          <span>• termination_without_cause</span>
          <span>• termination_with_cause</span>
          <span>• termination_coach_initiated</span>
          <span>• mitigation_required</span>
          <span>• status</span>
          <span>• notes</span>
        </div>
      </div>

      {/* Drop zone */}
      <div
        className="flex flex-col items-center justify-center gap-2 rounded-md border-2 border-dashed p-8 text-center cursor-pointer hover:border-primary/50 hover:bg-muted/30 transition-colors"
        onDragOver={(e) => e.preventDefault()}
        onDrop={handleDrop}
        onClick={() => fileInputRef.current?.click()}
      >
        <Upload className="h-8 w-8 text-muted-foreground" />
        {selectedFile ? (
          <div className="space-y-1">
            <p className="text-sm font-medium">{selectedFile.name}</p>
            <p className="text-xs text-muted-foreground">{(selectedFile.size / 1024).toFixed(1)} KB · Click to change</p>
          </div>
        ) : (
          <div className="space-y-1">
            <p className="text-sm font-medium">Drop your CSV file here</p>
            <p className="text-xs text-muted-foreground">or click to browse · Max 5 MB</p>
          </div>
        )}
        <input ref={fileInputRef} type="file" accept=".csv" className="hidden" onChange={handleFileChange} />
      </div>

      {/* Preview action */}
      {selectedFile && !importResult && (
        <div className="flex gap-2">
          <Button variant="outline" onClick={handlePreview} disabled={isPreviewing} className="flex-1">
            {isPreviewing ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Validating…</> : <><Eye className="h-4 w-4 mr-2" />Preview & Validate</>}
          </Button>
          {preview && preview.errorCount === 0 && (
            <Button onClick={handleImport} disabled={isImporting} className="flex-1">
              {isImporting ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Importing…</> : <>Import {preview.validCount} contracts</>}
            </Button>
          )}
        </div>
      )}

      {/* Preview table */}
      {preview && (
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium">Validation Preview</span>
            <div className="flex gap-2">
              <Badge variant="outline" className="bg-green-100 text-green-800 border-green-200 gap-1">
                <CheckCircle2 className="h-3 w-3" /> {preview.validCount} valid
              </Badge>
              {preview.errorCount > 0 && (
                <Badge variant="outline" className="bg-red-100 text-red-800 border-red-200 gap-1">
                  <XCircle className="h-3 w-3" /> {preview.errorCount} errors
                </Badge>
              )}
            </div>
          </div>

          <div className="border rounded-md overflow-auto max-h-96">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-12">Row</TableHead>
                  <TableHead>Coach Email</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Salary</TableHead>
                  <TableHead>Term</TableHead>
                  <TableHead>Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {preview.rows.map((row) => {
                  const hasErrors = row.errors.length > 0;
                  return (
                    <TableRow key={row.row} className={hasErrors ? "bg-red-50/50" : ""}>
                      <TableCell className="text-muted-foreground text-xs">{row.row}</TableCell>
                      <TableCell>
                        <div className="text-xs">
                          <div className={hasErrors && row.errors.some(e => e.field === "coach_email") ? "text-red-600" : ""}>
                            {row.data.coach_email || "—"}
                          </div>
                          {row.coachName && (
                            <div className="text-green-700 flex items-center gap-1">
                              <CheckCircle2 className="h-3 w-3" /> {row.coachName}
                            </div>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="text-xs">{row.data.contract_type || "—"}</TableCell>
                      <TableCell className="text-xs">
                        {row.data.annual_salary ? `$${Number(row.data.annual_salary).toLocaleString()}` : "—"}
                      </TableCell>
                      <TableCell className="text-xs">
                        {row.data.start_date && row.data.end_date
                          ? `${row.data.start_date} → ${row.data.end_date}`
                          : "—"}
                      </TableCell>
                      <TableCell>
                        {hasErrors ? (
                          <div className="space-y-0.5">
                            {row.errors.map((e, i) => (
                              <div key={i} className="text-[10px] text-red-600 flex items-start gap-1">
                                <AlertTriangle className="h-3 w-3 shrink-0 mt-0.5" />
                                <span><strong>{e.field}:</strong> {e.message}</span>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <Badge className="text-[10px] bg-green-100 text-green-800 border-green-200 hover:bg-green-100">
                            Valid
                          </Badge>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>

          {preview.errorCount > 0 && (
            <div className="rounded-md bg-yellow-50 border border-yellow-200 p-3 text-sm text-yellow-800">
              <strong>{preview.errorCount} row{preview.errorCount !== 1 ? "s" : ""} have errors.</strong>{" "}
              Fix the issues in your CSV and re-upload to proceed.
            </div>
          )}

          {preview.errorCount === 0 && (
            <Button onClick={handleImport} disabled={isImporting} className="w-full">
              {isImporting ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Importing…</> : <>Import {preview.validCount} contracts</>}
            </Button>
          )}
        </div>
      )}

      {/* Import result */}
      {importResult && (
        <div className="rounded-md border overflow-hidden">
          <div className="flex items-center justify-between px-3 py-2 bg-muted/40 border-b">
            <span className="text-sm font-medium">Import Results</span>
            <div className="flex gap-2">
              <Badge variant="outline" className="bg-green-100 text-green-800 border-green-200 gap-1">
                <CheckCircle2 className="h-3 w-3" /> {importResult.imported} imported
              </Badge>
              {importResult.errors.length > 0 && (
                <Badge variant="outline" className="bg-red-100 text-red-800 border-red-200 gap-1">
                  <XCircle className="h-3 w-3" /> {importResult.errors.length} failed
                </Badge>
              )}
            </div>
          </div>
          {importResult.errors.length > 0 && (
            <div className="max-h-48 overflow-y-auto divide-y">
              {importResult.errors.map((err) => (
                <div key={err.row} className="px-3 py-2 text-xs flex gap-3">
                  <span className="text-muted-foreground shrink-0">Row {err.row}</span>
                  <span className="text-red-600">{err.message}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main Page
// ---------------------------------------------------------------------------

export default function ContractsImport() {
  const [, setLocation] = useLocation();
  const { org } = useOrg();
  const aiImport = useFeature("contracts.ai_import");
  // Demo orgs never see the pricing/upgrade upsell — billing is managed by
  // the Zindeal account team.
  const isDemo = Boolean(org?.isDemo);

  const { toast } = useToast();

  useEffect(() => {
    if (org && org.role !== "admin") {
      toast({ title: "Access denied", description: "Only admins can import contracts.", variant: "destructive" });
      setLocation("/contracts");
    }
  }, [org, setLocation, toast]);

  return (
    <div className="space-y-6 max-w-7xl mx-auto">
      <div className="flex items-center gap-4">
        <Button variant="outline" size="icon" asChild>
          <Link href="/contracts"><ArrowLeft className="h-4 w-4" /></Link>
        </Button>
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight">Import Contracts</h1>
          <p className="text-muted-foreground mt-1">
            {aiImport.enabled
              ? "Upload contract PDFs for AI-assisted extraction, or import in bulk via spreadsheet."
              : "Import contracts in bulk via spreadsheet."}
          </p>
        </div>
      </div>

      <div className="rounded-lg border bg-amber-50/60 border-amber-200 p-3 flex items-start gap-2 text-sm text-amber-900">
        <Info className="h-4 w-4 shrink-0 mt-0.5 text-amber-600" />
        <div>
          <strong>AI extraction accuracy note:</strong> Extracted values are proposals only — language and layouts vary across contracts.
          Always review every field before saving. Confidence badges and source tooltips help you spot where to double-check.
        </div>
      </div>

      <Tabs defaultValue={aiImport.enabled ? "pdf" : "spreadsheet"}>
        <TabsList className="w-full sm:w-auto">
          {aiImport.enabled ? (
            <TabsTrigger value="pdf" className="flex-1 sm:flex-none gap-2">
              <FileText className="h-4 w-4" />
              Upload PDFs
            </TabsTrigger>
          ) : (
            <Tooltip>
              <TooltipTrigger asChild>
                <div className="flex-1 sm:flex-none">
                  <TabsTrigger value="pdf" className="w-full gap-2 opacity-50 cursor-not-allowed" disabled>
                    <FileText className="h-4 w-4" />
                    Upload PDFs
                    <span className="ml-1 text-[9px] font-bold uppercase bg-amber-100 text-amber-700 border border-amber-200 px-1 rounded-sm">Draft</span>
                  </TabsTrigger>
                </div>
              </TooltipTrigger>
              <TooltipContent>
                <p className="text-xs">AI PDF extraction requires the Draft plan.</p>
              </TooltipContent>
            </Tooltip>
          )}
          <TabsTrigger value="spreadsheet" className="flex-1 sm:flex-none gap-2">
            <Upload className="h-4 w-4" />
            Upload Spreadsheet
          </TabsTrigger>
        </TabsList>

        {aiImport.enabled && (
          <TabsContent value="pdf" className="mt-4">
            <Card>
              <CardHeader>
                <CardTitle>AI-Assisted PDF Import</CardTitle>
                <CardDescription>
                  Drop one or many contract PDFs. The AI will extract fields automatically — you review and confirm each one before anything is saved.
                  Text-based PDFs only (not scanned images).
                </CardDescription>
              </CardHeader>
              <CardContent>
                <PdfTab />
              </CardContent>
            </Card>
          </TabsContent>
        )}

        {!aiImport.enabled && (
          <TabsContent value="pdf" className="mt-4">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  AI-Assisted PDF Import
                  <span className="text-xs font-bold uppercase bg-amber-100 text-amber-700 border border-amber-200 px-2 py-0.5 rounded-full">Draft plan</span>
                </CardTitle>
                <CardDescription>
                  Let AI extract contract fields directly from your PDF documents.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="rounded-xl border border-amber-200 bg-gradient-to-br from-amber-50 to-white p-6 text-center space-y-3">
                  {isDemo ? (
                    <p className="text-sm text-gray-600 max-w-xs mx-auto">
                      AI PDF extraction automatically pulls contract fields from
                      uploaded PDFs. Talk to your Zindeal account team to enable it.
                    </p>
                  ) : (
                    <>
                      <p className="text-sm text-gray-600 max-w-xs mx-auto">
                        AI PDF extraction is a <strong>Draft plan</strong> feature. Upgrade to automatically pull contract fields from uploaded PDFs.
                      </p>
                      <Button asChild size="sm" className="bg-[hsl(220,65%,12%)] hover:bg-[hsl(220,65%,7%)] text-white">
                        <a href="/settings#plans">Upgrade to Draft</a>
                      </Button>
                    </>
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        )}

        <TabsContent value="spreadsheet" className="mt-4">
          <Card>
            <CardHeader>
              <CardTitle>Spreadsheet Import</CardTitle>
              <CardDescription>
                Import contracts in bulk via CSV. Supports all contract fields including bonuses, buyout schedule, and termination rules.
                Each row must reference an existing coach by email.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <SpreadsheetTab />
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
